import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, doc, getDoc } from 'firebase/firestore';

// Helper function to get the last 7 dates
const getLastSevenDays = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        dates.push(d.toISOString().split('T')[0]); // YYYY-MM-DD
    }
    return dates;
};

const WeeklySummaryView = ({ customerId }) => {
    const [weeklyProgress, setWeeklyProgress] = useState([]);
    const [userGoalsAndStats, setUserGoalsAndStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchWeeklyData = async () => {
            if (!customerId) return;
            setLoading(true);
            setError(null);

            try {
                // --- 1. Fetch Daily Progress Data (meal adherence, daily logs) ---
                const dates = getLastSevenDays();
                const progressCollectionRef = collection(db, 'progress', customerId, 'dailyProgress');
                
                // Fetch progress documents for the last 7 days
                const progressPromises = dates.map(date => getDoc(doc(progressCollectionRef, date)));
                const progressDocSnaps = await Promise.all(progressPromises);

                const progressData = progressDocSnaps.map((snap, index) => {
                    // Use a safe default {} if the document doesn't exist to prevent crash
                    const data = snap.exists() ? snap.data() : {}; 
                    
                    let mealsCompleted = 0;
                    let mealsTotal = 0;
                    
                    // ⭐️ UPDATED LOGIC: Check for 'mealAdherence' field
                    if (data?.mealAdherence && typeof data.mealAdherence === 'object') {
                        const mealEntries = Object.values(data.mealAdherence);
                        mealsTotal = mealEntries.length;
                        // Count how many are 'true'
                        mealsCompleted = mealEntries.filter(completed => completed === true).length;
                    }

                    return {
                        date: dates[index],
                        stats: {
                            weight: data?.currentWeight || 0,
                            sleepTime: data?.sleepTime || 0,
                            waterIntake: data?.waterIntake || 0,
                            exerciseTime: data?.exerciseTime || 0,
                        },
                        mealStats: { mealsCompleted, mealsTotal }, 
                    };
                });
                setWeeklyProgress(progressData);

                // --- 2. Fetch Overall User Stats and Goals ---
                const userStatsDocRef = doc(db, 'userStats', customerId);
                const userStatsDocSnap = await getDoc(userStatsDocRef);
                if (userStatsDocSnap.exists()) {
                    setUserGoalsAndStats(userStatsDocSnap.data());
                } else {
                    setUserGoalsAndStats({});
                }

            } catch (err) {
                console.error("Error fetching weekly summary data:", err);
                setError("Failed to load weekly summary.");
            } finally {
                setLoading(false);
            }
        };

        fetchWeeklyData();
    }, [customerId]);

    if (loading) return <p>Loading weekly summary...</p>;
    if (error) return <p className="error-message">{error}</p>;
    
    // Filter to only include days with at least one logged stat/meal
    const loggedDays = weeklyProgress.filter(day => 
        day.mealStats.mealsTotal > 0 || 
        day.stats.weight > 0 || 
        day.stats.sleepTime > 0 || 
        day.stats.waterIntake > 0 ||
        day.stats.exerciseTime > 0
    );

    if (loggedDays.length === 0) return <p>No progress data found for the last 7 days.</p>;

    // Calculate Summary Stats
    const totalDays = 7;
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // ⭐️ MEAL ADHERENCE CALCULATION
    const totalMealsCompleted = loggedDays.reduce((sum, day) => sum + day.mealStats.mealsCompleted, 0);
    const totalMealsPossible = loggedDays.reduce((sum, day) => sum + day.mealStats.mealsTotal, 0);
    const mealAdherencePercentage = totalMealsPossible > 0 
        ? ((totalMealsCompleted / totalMealsPossible) * 100).toFixed(0) 
        : 'N/A';
    
    // Calculate weight change (simple logic: oldest logged weight vs most recent logged weight)
    const sortedLoggedDays = [...loggedDays].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    const initialLoggedWeight = sortedLoggedDays.find(d => d.stats.weight > 0)?.stats.weight || userGoalsAndStats?.initialWeight || 0;
    const currentLoggedWeight = [...sortedLoggedDays].reverse().find(d => d.stats.weight > 0)?.stats.weight || userGoalsAndStats?.currentWeight || 0;

    const weightChange = (initialLoggedWeight > 0 && currentLoggedWeight > 0) 
        ? (initialLoggedWeight - currentLoggedWeight).toFixed(1) 
        : 'N/A';
    
    // Average Stats
    const totalWeight = loggedDays.reduce((sum, d) => sum + d.stats.weight, 0);
    const totalSleep = loggedDays.reduce((sum, d) => sum + d.stats.sleepTime, 0);
    const totalWater = loggedDays.reduce((sum, d) => sum + d.stats.waterIntake, 0);
    const totalExercise = loggedDays.reduce((sum, d) => sum + d.stats.exerciseTime, 0);
    const daysWithStats = loggedDays.length;

    const avgWeight = daysWithStats > 0 ? (totalWeight / daysWithStats).toFixed(1) : 'N/A';
    const avgSleep = daysWithStats > 0 ? (totalSleep / daysWithStats).toFixed(1) : 'N/A';
    const avgWater = daysWithStats > 0 ? (totalWater / daysWithStats).toFixed(1) : 'N/A';
    const avgExercise = daysWithStats > 0 ? Math.round(totalExercise / daysWithStats) : 'N/A';

    return (
        <div className="weekly-summary-view" style={{ marginTop: '20px', border: '1px solid #ddd', padding: '15px', borderRadius: '8px' }}>
            <h4>📅 Last 7 Days Summary</h4>

            <div className="summary-highlights" style={{ display: 'flex', gap: '20px', marginBottom: '15px', flexWrap: 'wrap' }}>
                <div style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', flex: 1, minWidth: '150px' }}>
                    <strong>Days Logged:</strong> {loggedDays.length} / {totalDays}
                </div>
                
                {/* ⭐️ MEAL ADHERENCE SUMMARY */}
                <div style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', flex: 1, minWidth: '150px' }}>
                    <strong>Meal Adherence:</strong> <span style={{ fontWeight: 'bold', color: mealAdherencePercentage !== 'N/A' && mealAdherencePercentage >= 70 ? 'green' : '#333' }}>{mealAdherencePercentage}%</span> 
                    <small>({totalMealsCompleted} / {totalMealsPossible} meals)</small>
                </div>
                {/* END MEAL ADHERENCE SUMMARY */}

                <div style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', flex: 1, minWidth: '150px' }}>
                    <strong>Weight Change:</strong> <span style={{ color: weightChange !== 'N/A' && weightChange >= 0 ? 'green' : 'red' }}>{weightChange} kgs</span>
                </div>
            </div>

            <div className="average-stats" style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', justifyContent: 'space-around' }}>
                <p>Avg Sleep: <strong>{avgSleep} hrs</strong></p>
                <p>Avg Water: <strong>{avgWater} L</strong></p>
                <p>Avg Exercise: <strong>{avgExercise} min</strong></p>
            </div>

            <h5 style={{ borderBottom: '1px solid #eee', paddingBottom: '5px' }}>Daily Breakdown:</h5>
            <div className="daily-breakdown-table" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f0f0f0' }}>
                            <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>Date</th>
                            <th style={{ padding: '8px', border: '1px solid #ddd' }}>Meals (C/T)</th> 
                            <th style={{ padding: '8px', border: '1px solid #ddd' }}>Weight</th>
                            <th style={{ padding: '8px', border: '1px solid #ddd' }}>Sleep</th>
                            <th style={{ padding: '8px', border: '1px solid #ddd' }}>Water</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Show only logged days, most recent first */}
                        {[...loggedDays].reverse().map((day, index) => (
                            <tr key={day.date} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{day.date} ({daysOfWeek[new Date(day.date).getDay()]})</td>
                                {/* ⭐️ DISPLAY MEAL ADHERENCE STATS */}
                                <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                                    {day.mealStats.mealsTotal > 0 ? `${day.mealStats.mealsCompleted} / ${day.mealStats.mealsTotal}` : '-'}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>{day.stats.weight > 0 ? day.stats.weight.toFixed(1) : '-'}</td>
                                <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>{day.stats.sleepTime > 0 ? day.stats.sleepTime.toFixed(1) : '-'}</td>
                                <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>{day.stats.waterIntake > 0 ? day.stats.waterIntake.toFixed(1) : '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            {loggedDays.length === 0 && <p style={{textAlign: 'center', color: '#888'}}>No stats recorded in the last 7 days.</p>}

        </div>
    );
};

export default WeeklySummaryView;